/**
 * The arithmetic behind DonutChart, kept apart from its markup.
 *
 * Its own module for the same reason `icon-paths` is: a component file that
 * exports anything other than components loses Fast Refresh. It is also the
 * part that can be wrong without looking wrong - a wedge a few degrees out
 * still draws a perfectly pleasant ring, it just misstates the business - so
 * it is worth being able to test on its own.
 */

/** Where the ring is drawn. Lengths are in the SVG's own units. */
export interface DonutGeometry {
  radius: number;
  thickness: number;
  centre: number;
  /** Arc length of the parting between two wedges, measured on the rim. */
  gap: number;
}

export interface DonutSegment {
  /** Share of the whole, 0-1. */
  percent: number;
  /**
   * Null for a slice of nothing. A zero still earns a legend row - "0 overdue"
   * is an answer somebody came here for - but drawing a zero-width wedge left
   * a hairline on the rim that read as a real, very small category.
   */
  path: string | null;
}

const FULL_TURN = 360;

/** Twelve o'clock, because SVG angles start at three. */
const START_ANGLE = -90;

/**
 * Every share of the whole, measured onto the items it was given.
 *
 * The geometry is added to each item rather than returned beside it, so a
 * caller never re-pairs two lists by index - the one place a zero-value slice
 * that is drawn differently from the way it is labelled could creep in.
 *
 * Only ever pass a set that genuinely partitions its whole. Overlapping counts
 * - open work, and the overdue subset of it - drawn as one ring add up to more
 * than everything and quietly misstate every proportion on the page.
 */
export function donutSegments<T extends { value: number }>(
  items: readonly T[],
  geometry: DonutGeometry,
): (T & DonutSegment)[] {
  const total = items.reduce((sum, item) => sum + Math.max(item.value, 0), 0);

  if (total <= 0) {
    return items.map((item) => ({ ...item, percent: 0, path: null }));
  }

  const drawn = items.filter((item) => item.value > 0).length;

  /* A parting belongs between two wedges. Insetting the single wedge of a
     whole would notch what is really an unbroken ring, and that notch reads
     as a second category that is not there. */
  const partingDegrees =
    drawn > 1 && geometry.radius > 0
      ? (geometry.gap / (2 * Math.PI * geometry.radius)) * FULL_TURN
      : 0;

  let angle = START_ANGLE;

  return items.map((item) => {
    const size = Math.max(item.value, 0);
    const percent = size / total;
    const sweep = percent * FULL_TURN;
    const from = angle;

    angle += sweep;

    if (size <= 0) {
      return { ...item, percent: 0, path: null };
    }

    /* Never take more than a third of a wedge for the parting: a one-percent
       slice was being inset out of existence, leaving a gap on the rim where
       a real - and usually urgent - category lived. */
    const inset = Math.min(partingDegrees / 2, sweep / 3);

    return { ...item, percent, path: arcPath(from + inset, from + sweep - inset, geometry) };
  });
}

function arcPath(from: number, to: number, geometry: DonutGeometry): string {
  /* Two identical endpoints draw nothing at all, so a whole ring stops just
     short of the full turn. The seam is a hundredth of a degree - narrower
     than a pixel at any size this renders at. */
  const sweep = Math.min(to - from, FULL_TURN - 0.01);
  const end = from + sweep;
  const inner = Math.max(geometry.radius - geometry.thickness, 0);
  const large = sweep > 180 ? 1 : 0;

  const outerStart = point(geometry.centre, geometry.radius, from);
  const outerEnd = point(geometry.centre, geometry.radius, end);
  const innerEnd = point(geometry.centre, inner, end);
  const innerStart = point(geometry.centre, inner, from);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${geometry.radius} ${geometry.radius} 0 ${large} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${inner} ${inner} 0 ${large} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

function point(centre: number, radius: number, degrees: number): { x: string; y: string } {
  const radians = (degrees * Math.PI) / 180;

  /* Three decimals: enough for a sub-pixel path, short enough that the `d`
     attribute is still readable when somebody inspects the element. */
  return {
    x: (centre + radius * Math.cos(radians)).toFixed(3),
    y: (centre + radius * Math.sin(radians)).toFixed(3),
  };
}
